// The WebGPU pipeline: the uniform packer, the vertex layout and the
// pipeline cache (the runtime design §2 and §5).
//
// `gl/program.ts` compiles two sources and looks a uniform up by name. This
// file cannot: WebGPU takes ONE module, the uniform block is one buffer at
// byte offsets, and the vertex layout and the blend state are baked into a
// RENDER PIPELINE object rather than set as state.
//
// So the class holds three things the WebGL2 program does not:
//
//  1. **A CPU shadow of the whole block.** Every setter writes into it at the
//     offset `wgslLayout` computed, so `setExtra`, the per-faction mesh
//     draws and the sweep loop keep writing by name and nothing above this
//     file changes. Each draw copies the shadow into the arena and takes a
//     dynamic offset back, so setters before a loop persist and each draw
//     snapshots.
//  2. **The vertex buffer layouts**, derived from the same `AttribSpec[]` the
//     GLSL declares and the WGSL header is generated from.
//  3. **A cache of render pipelines**, keyed by the attachments of the open
//     pass and by the one blend flag. `#blend=0` turns blend off, and it
//     gives one more cache entry.

import { wgslLayout, type Member, type UniformLayout } from '../shaders/wgsl/layout';
import type { BindGroupKind } from '../shaders/wgsl/bindings';
import type { UniformSpec } from '../shaders/prelude';
import type { AttribSpec, Pipeline } from '../gfx/types';
import type { ProgramSource } from '../shaders/program';
import { CORNER_LOCATION } from '../gl/corner';
import { alignSlot } from './arena';
import { corners, type GpuCornerBuffer } from './corner';
import type { DrawBinding, Gpu, GpuResource } from './context';

/** The name of the atlas sampler. It alone picks the `sprite` layout. */
const ATLAS_SAMPLER = 'uTex';

/** The corner's own name. It alone marks a per-VERTEX first attribute. */
export const CORNER_NAME = 'aCorner';

/**
 * The one blend state of the engine.
 *
 * Every fragment shader writes premultiplied rgba, and additive glow is a
 * per-instance flag rather than a second state (`layer.ts` `F_ADD`). It is
 * the `blendFunc(ONE, ONE_MINUS_SRC_ALPHA)` of `gl/context.ts`, written the
 * way WebGPU states it.
 */
export const BLEND_STATE: GPUBlendState = {
  color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
  alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
};

/** Every colour channel. The engine masks none of them. */
const WRITE_ALL = 0xf;

/**
 * The descriptor of one render pipeline.
 *
 * The runtime builds a pipeline per (pass, blend) pair below, and the device
 * compile check of `dev/wgsl-check.html` builds the same descriptor for every
 * program of `ENGINE_PROGRAMS`. The two must not drift: a check that built a
 * descriptor of its own would pass while the runtime fails. So the build is
 * this ONE pure function, and both callers read it.
 */
export function pipelineDescriptor(args: {
  readonly name: string;
  /** The pipeline-cache key, for the label alone. */
  readonly key: string;
  readonly module: GPUShaderModule;
  readonly layout: GPUPipelineLayout;
  readonly shape: VertexShape;
  /** The attachments of the pass this pipeline draws into. */
  readonly formats: readonly GPUTextureFormat[];
  readonly blend: boolean;
}): GPURenderPipelineDescriptor {
  return {
    label: `fast: ${args.name} ${args.key}`,
    layout: args.layout,
    vertex: {
      module: args.module,
      entryPoint: 'vs',
      buffers: args.shape.buffers as GPUVertexBufferLayout[],
    },
    fragment: {
      module: args.module,
      entryPoint: 'fs',
      targets: args.formats.map((format) => ({
        format,
        ...(args.blend ? { blend: BLEND_STATE } : {}),
        writeMask: WRITE_ALL,
      })),
    },
    primitive: { topology: args.shape.topology },
  };
}

/** The vertex format of an attribute of N floats. */
function vertexFormat(size: number): GPUVertexFormat {
  switch (size) {
    case 1: return 'float32';
    case 2: return 'float32x2';
    case 3: return 'float32x3';
    case 4: return 'float32x4';
    default: throw new Error(`fast: no vertex format for ${size} floats`);
  }
}

/** How many floats one element of a list takes: the end of its last member. */
export function strideFloats(attribs: readonly AttribSpec[]): number {
  let end = 0;
  for (const a of attribs) end = Math.max(end, a.offset + a.size);
  return end;
}

/** The vertex buffers and the topology one attribute list asks for. */
export interface VertexShape {
  readonly buffers: readonly GPUVertexBufferLayout[];
  readonly topology: GPUPrimitiveTopology;
  /** Does the draw come from an index buffer? A mesh does. */
  readonly indexed: boolean;
}

/**
 * The vertex layout of one program.
 *
 * A list that starts with the corner is a four-corner STRIP: buffer 0 is the
 * shared corner buffer, stepped per vertex, and every other attribute rides
 * buffer 1, stepped per INSTANCE. A list that does not is a MESH: one buffer
 * of per-vertex attributes, drawn from an index buffer as a triangle list.
 */
export function vertexShape(attribs: readonly AttribSpec[]): VertexShape {
  const head = attribs[0];
  const corner = !!head && head.location === CORNER_LOCATION && head.name === CORNER_NAME;
  if (!corner) {
    return { buffers: [bufferLayout(attribs, 'vertex')], topology: 'triangle-list', indexed: true };
  }
  const buffers = [bufferLayout([head], 'vertex')];
  const rest = attribs.slice(1);
  if (rest.length > 0) buffers.push(bufferLayout(rest, 'instance'));
  return { buffers, topology: 'triangle-strip', indexed: false };
}

/** One vertex buffer: every attribute of a list, at the list's own stride. */
function bufferLayout(
  attribs: readonly AttribSpec[], stepMode: GPUVertexStepMode,
): GPUVertexBufferLayout {
  return {
    arrayStride: strideFloats(attribs) * 4,
    stepMode,
    attributes: attribs.map((a) => ({
      shaderLocation: a.location, offset: a.offset * 4, format: vertexFormat(a.size),
    })),
  };
}

/**
 * Which of the three bind-group layouts one program takes.
 *
 * A sampler that is not the atlas is a per-cell plane, and a plane means the
 * whole `world` group. The atlas alone means `sprite`. No sampler at all
 * means `none`, and such a pipeline binds group 1 not at all.
 */
export function layoutKindOf(specs: readonly UniformSpec[]): BindGroupKind {
  let sprite = false;
  for (const spec of specs) {
    if (spec.type !== 'sampler') continue;
    if (spec.name === ATLAS_SAMPLER) { sprite = true; continue; }
    return 'world';
  }
  return sprite ? 'sprite' : 'none';
}

export class GpuPipeline implements GpuResource, Pipeline, DrawBinding {
  readonly name: string;
  readonly layout: UniformLayout;
  readonly kind: BindGroupKind;

  private readonly members = new Map<string, Member>();
  private readonly names = new Set<string>();
  private readonly shape: VertexShape;
  /** The block, and the two views the setters write through. */
  private readonly block: ArrayBuffer;
  private readonly bytes: Uint8Array;
  private readonly f32: Float32Array;
  private readonly i32: Int32Array;
  /** The arena slice one draw takes: the block, rounded to the slot. */
  private readonly slice: number;

  private module: GPUShaderModule | null = null;
  /**
   * The render pipelines by the key of their pass target, one map with the
   * blend off and one with it on, so a draw looks a pipeline up with no
   * key string of its own (§10).
   */
  private readonly cache = [
    new Map<string, GPURenderPipeline>(), new Map<string, GPURenderPipeline>(),
  ] as const;
  private corner: GpuCornerBuffer | null = null;

  constructor(private readonly gpu: Gpu, private readonly src: ProgramSource) {
    this.name = src.name;
    this.layout = wgslLayout(src.uniforms);
    this.kind = layoutKindOf(src.uniforms);
    this.shape = vertexShape(src.attribs);
    for (const m of this.layout.members) this.members.set(m.name, m);
    // A SAMPLER is no member of the block, but `has()` must still answer
    // true for it: `WorldPass.bind` asks before it binds a plane.
    for (const spec of src.uniforms) this.names.add(spec.name);
    this.block = new ArrayBuffer(this.layout.size);
    this.bytes = new Uint8Array(this.block);
    this.f32 = new Float32Array(this.block);
    this.i32 = new Int32Array(this.block);
    this.slice = alignSlot(this.layout.size);
    gpu.register(this);
  }

  create(device: GPUDevice): void {
    this.destroy();
    if (!this.src.wgsl) {
      // The twin is not written yet. It is a clear stop behind the knob and
      // never a blank picture: `main.ts` catches it and shows the stop.
      throw new Error(`fast: ${this.name} has no WGSL twin yet (stage 1b or 2)`);
    }
    this.module = device.createShaderModule({ label: this.name, code: this.src.wgsl });
  }

  destroy(): void {
    this.module = null;
    this.cache[0].clear();
    this.cache[1].clear();
  }

  /** The packed block, exactly as a draw pushes it. The test reads it. */
  get uniformBytes(): Uint8Array { return this.bytes; }

  use(): boolean {
    if (this.gpu.lost || !this.module) return false;
    this.gpu.current = this;
    return true;
  }

  has(name: string): boolean { return this.names.has(name); }

  // -- the setters -----------------------------------------------------------
  //
  // Each writes into the shadow at the offset the layout gave it. A name the
  // block does not carry returns at once, so a shared uniform contract is
  // safe to over-state, exactly as it is on WebGL2.

  uniform1i(name: string, v: number) {
    const m = this.members.get(name); if (!m) return;
    this.i32[m.offset >> 2] = v | 0;
  }

  uniform1f(name: string, v: number) {
    const m = this.members.get(name); if (!m) return;
    this.f32[m.offset >> 2] = v;
  }

  uniform2f(name: string, x: number, y: number) {
    const m = this.members.get(name); if (!m) return;
    const at = m.offset >> 2;
    this.f32[at] = x; this.f32[at + 1] = y;
  }

  uniform2i(name: string, x: number, y: number) {
    const m = this.members.get(name); if (!m) return;
    const at = m.offset >> 2;
    this.i32[at] = x | 0; this.i32[at + 1] = y | 0;
  }

  uniform3f(name: string, x: number, y: number, z: number) {
    const m = this.members.get(name); if (!m) return;
    const at = m.offset >> 2;
    this.f32[at] = x; this.f32[at + 1] = y; this.f32[at + 2] = z;
  }

  uniform4f(name: string, x: number, y: number, z: number, w: number) {
    const m = this.members.get(name); if (!m) return;
    const at = m.offset >> 2;
    this.f32[at] = x; this.f32[at + 1] = y; this.f32[at + 2] = z; this.f32[at + 3] = w;
  }

  /** A `float[]`, packed four to a `vec4f` and so DENSE in bytes. */
  uniform1fv(name: string, v: Float32Array) { this.dense(name, v); }

  /** A `vec3[]`: the uniform address space gives every element 16 bytes. */
  uniform3fv(name: string, v: Float32Array) {
    const m = this.members.get(name); if (!m) return;
    const count = Math.min(Math.floor(v.length / 3), m.count ?? 1);
    for (let i = 0; i < count; i++) {
      const at = (m.offset + 16 * i) >> 2;
      this.f32[at] = v[3 * i];
      this.f32[at + 1] = v[3 * i + 1];
      this.f32[at + 2] = v[3 * i + 2];
    }
  }

  /** A `vec4[]`: four floats an element, and so contiguous. */
  uniform4fv(name: string, v: Float32Array) { this.dense(name, v); }

  /** Copies the floats of an array member as they stand, up to its size. */
  private dense(name: string, v: Float32Array): void {
    const m = this.members.get(name); if (!m) return;
    const at = m.offset >> 2;
    const n = Math.min(v.length, (m.size / 4) | 0);
    for (let i = 0; i < n; i++) this.f32[at + i] = v[i];
  }

  /**
   * A `mat3`.
   *
   * GLSL packs a `mat3` as nine floats, column-major, so element `3c + r` is
   * row `r` of column `c`. WGSL gives every column of a `mat3x3f` SIXTEEN
   * bytes, so the same element lands at `offset + 16c + 4r`.
   */
  uniformMatrix3fv(name: string, v: Float32Array) {
    const m = this.members.get(name); if (!m) return;
    for (let c = 0; c < 3; c++) {
      const at = (m.offset + 16 * c) >> 2;
      this.f32[at] = v[3 * c];
      this.f32[at + 1] = v[3 * c + 1];
      this.f32[at + 2] = v[3 * c + 2];
    }
  }

  /**
   * Points a sampler at a texture unit.
   *
   * It does nothing at all. A WebGPU texture is bound by its UNIT already —
   * `bind(unit)` writes the view into the context's table and the binding
   * number is the unit — so there is no number to write into the block.
   */
  sampler(_name: string, _unit: number) { /* the unit IS the binding */ }

  // -- the draw --------------------------------------------------------------

  /**
   * Sets the pipeline, pushes the block and binds both groups.
   *
   * Every draw of the backend goes through it: `drawQuad` here, and
   * `InstanceBuffer.draw` and `MeshBuffer.draw` through `gpu.current`.
   */
  bindDraw(): GPURenderPassEncoder | null {
    const gpu = this.gpu;
    if (gpu.lost) return null;
    const pass = gpu.frame.pass;
    if (!pass) return null;
    const rp = this.renderPipeline();
    if (!rp) return null;
    const offset = gpu.arena.push(this.bytes);
    if (offset < 0) return null;
    const group0 = gpu.groups.group0(this.slice);
    if (!group0) return null;
    pass.setPipeline(rp);
    pass.setBindGroup(0, group0, [offset]);
    if (this.kind !== 'none') {
      const group1 = gpu.groups.group1(this.kind);
      if (!group1) return null;
      pass.setBindGroup(1, group1);
    }
    return pass;
  }

  /** Draws the shared four-corner strip once (R553). */
  drawQuad(): void {
    const pass = this.bindDraw();
    if (!pass) return;
    const corner = (this.corner ?? (this.corner = corners(this.gpu))).buffer;
    if (!corner) return;
    pass.setVertexBuffer(0, corner);
    pass.draw(4, 1);
    this.gpu.drawCalls++;
    this.gpu.instances++;
  }

  /** Drops the pipeline. The context destroys it and forgets it. */
  dispose(): void { this.gpu.unregister(this); }

  /**
   * The render pipeline for the pass that is open and the blend flag now.
   *
   * The attachments of a pass are part of a pipeline in WebGPU, so the
   * cache keys a pipeline by the pass it draws into. Each one is built once
   * and kept.
   */
  private renderPipeline(): GPURenderPipeline | null {
    const gpu = this.gpu;
    const module = this.module;
    if (!module) return null;
    const target = gpu.frame.passTarget;
    const blend = gpu.frame.blend;
    const byTarget = this.cache[blend ? 1 : 0];
    const held = byTarget.get(target.key);
    if (held) return held;
    const made = gpu.device.createRenderPipeline(pipelineDescriptor({
      name: this.name,
      key: `${target.key}|${blend ? 1 : 0}`,
      module,
      layout: gpu.groups.pipelineLayout(this.kind),
      shape: this.shape,
      formats: target.formats,
      blend,
    }));
    byTarget.set(target.key, made);
    return made;
  }
}
